import React, { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../services/auth";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import LoanForm from "./LoanForm";
import LoanView from "./LoanView";
import { useCombinedContext } from "../../context/CombinedContext";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";


const LoanList = () => {
  const [loan, setLoan] = useState([]);
  const [employee, setEmployee] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // All | Pending | Approved | Rejected
  const [fromDate, setFromDate] = useState(""); // Start date for filtering
  const [toDate, setToDate] = useState(""); // End date for filtering
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedYear, setSelectedYear] = useState(""); // For year-wise filtering
  const [loading, setLoading] = useState(false);
  const { selectesEmployee, selectedCompany } = useCombinedContext();
  const companyId = selectedCompany?._id;
  const companyCode = selectedCompany?.company_code;
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // New state for status confirmation modal
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  const [statusLoan, setStatusLoan] = useState(null);

  // console.log(loan, "Loan");
  const fetchLoan = async () => {
    try {
      setLoading(true);
      const params = {};
      if (fromDate) params.start_date = fromDate;
      if (toDate) params.end_date = toDate;
      const response = await axiosInstance.get(`/loan/report/${companyId}`, {
        params,
      });

      const sortedData = (response.data.data || []).sort(
        (b, a) => new Date(b.loanDate) - new Date(a.loanDate), // Replace with b.createdAt if needed
      );
      setLoan(sortedData || []);
      // console.log(response.data.data);
    } catch (error) {
      toast.error("Error fetching loan");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployee = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/loan/employee/${companyId}`);
      setEmployee(response.data.employees || []);
    } catch (error) {
      toast.error("Error fetching Employee");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchLoan();
    }
  }, [companyId, fromDate, toDate]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(0);
  };

  // Search + status filtering, derived from the fetched loans so both
  // controls always stay in sync (employee number may come back as
  // employeeNo or employeeCode).
  const filteredLoans = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    if (!text && statusFilter === "All") return loan;

    return loan.filter((item) => {
      const name = (item.employee?.name ?? "").toString().toLowerCase();
      const empNo = (
        item.employee?.employeeNo ??
        item.employee?.employeeCode ??
        ""
      )
        .toString()
        .toLowerCase();
      const appNo = (item.applicationNo ?? "").toString().toLowerCase();

      const matchesSearch =
        !text ||
        name.includes(text) ||
        empNo.includes(text) ||
        appNo.includes(text);

      const matchesStatus =
        statusFilter === "All" || (item.status || "Pending") === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [loan, searchText, statusFilter]);

  const handleDelete = (id) => {
    setDeleteTarget(id);
    setIsDeleteModalOpen(true);
  };
  // console.log("delete",deleteTarget)
  // console.log("loan",loan)
  const confirmDelete = async () => {
    if (!deleteTarget || !deleteTarget.loanItem) return;

    const { loanDocId, loanId } = deleteTarget.loanItem;

    try {
      await axiosInstance.delete(`/loan/${loanDocId}/${loanId}`);

      setLoan((prev) => prev.filter((loc) => loc.loanId !== loanId));

      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
      toast.success("Loan deleted successfully");
    } catch (error) {
      toast.error(error.message[1]);
      // console.error(error);
    }
  };

  // Get data from selected year
  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    if (year) {
      setFromDate(`${year}-01-01`);
      setToDate(`${year}-12-31`);
    } else {
      setFromDate("");
      setToDate("");
    }
  };

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  // const itemsPerPage = 5;
  const currentLoanItems = [...filteredLoans]
    .reverse()
    .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
  const pageCount = Math.ceil(filteredLoans.length / itemsPerPage);

  // Check Role
  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));
  const userRole = authData.role;

  const updateLoanStatus = async (loanId, status, loanDocId) => {
    try {
      await axiosInstance.post(`/loan/loan-approval/${loanDocId}/${loanId}`, {
        status,
      });
      toast.success(`Loan ${status.toLowerCase()} successfully`);
      fetchLoan();
    } catch (error) {
      toast.error(`Failed to ${status.toLowerCase()} loan`);
    }
  };

  // New: Handle status change for pending loans (approve/reject) with custom confirmation modal
  const handleStatusChange = (loanItem, action) => {
    if (loanItem.status !== "Pending") {
      toast.info("Only pending loans can be updated.");
      return;
    }
    setStatusLoan(loanItem);
    setStatusAction(action);
    setIsStatusModalOpen(true);
  };

  const confirmStatusChange = async () => {
    if (!statusLoan) return;

    const loanDocId = statusLoan.loanDocId || statusLoan._id; // adjust as per your structure
    const loanId = statusLoan.loanId;

    await updateLoanStatus(loanId, statusAction, loanDocId);
    setIsStatusModalOpen(false);
    setStatusLoan(null);
    setStatusAction(null);
  };

  const cancelStatusChange = () => {
    setIsStatusModalOpen(false);
    setStatusLoan(null);
    setStatusAction(null);
  };
  function formatPrice(price) {
    const num = parseFloat(price);
    if (isNaN(num)) return "N/A";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return (
    <div>
      <ToastContainer />
      <div className="mb-4 headStyle d-flex  justify-content-between align-items-cente">
        <h4>Loan</h4>
        <button
          className="button-2"
          onClick={() => {
            if (selectedCompany?.status === false) {
              toast.error(
                "The selected company is inactive. Cannot add a loan.",
              );
            } else {
              setSelectedData(null);
              setShowForm(true);
            }
          }}
          style={{
            borderRadius: "8px",
            whiteSpace: "nowrap",
            padding: "8px 16px",
          }}
        >
          Add Loan
        </button>
      </div>
      <div
        className="d-flex flex-wrap mt-3 align-items-center gap-3"
        style={{
          padding: "16px",
          boxShadow: "0px 0px 10px 0px #e0e0e0",
          background: "white",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <label htmlFor="fromDate" style={{ width: "-webkit-fill-available" }}>
            From Date
          </label>
          <input
            className="form-control"
            type="date"
            placeholder="From Date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              border: "1px solid #1E2A5E",
              borderRadius: "8px",
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <label htmlFor="toDate" style={{ width: "-webkit-fill-available" }}>
            To Date
          </label>
          <input
            className="form-control"
            type="date"
            placeholder="To Date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              border: "1px solid #1E2A5E",
              borderRadius: "8px",
            }}
          />
        </div>
        <div>
          <select
            className="form-select"
            value={selectedYear}
            onChange={handleYearChange}
            style={{
              width: "70px",
              maxHeight: "300px !important",
              overflowY: "auto",
            }}
          >
            <option value="">Year</option>
            {Array.from(
              { length: new Date().getFullYear() - 2000 + 1 },
              (_, i) => 2000 + i,
            ).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="headStyle mt-4">
        <div className="d-flex mt-2 flex-wrap align-items-center gap-3 justify-content-between">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div>
              <input
                className="form-control"
                type="search"
                placeholder="Search..."
                style={{
                  maxWidth: "100%",
                  border: "1px solid #1E2A5E",
                  borderRadius: "8px",
                }}
                onChange={handleSearchChange}
                value={searchText}
              />
            </div>
            <div className="d-flex align-items-center">
              <label className="me-2 pt-1">Count</label>
              <select
                className="form-select no-arrow"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(0);
                }}
                style={{ width: "auto" }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
                <option value="25">25</option>
                <option value="30">30</option>
              </select>
            </div>
            <div className="d-flex align-items-center">
              <label className="me-2 pt-1" htmlFor="loanStatusFilter">
                Status Filter
              </label>
              <select
                id="loanStatusFilter"
                className="form-select no-arrow"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(0);
                }}
                style={{ width: "auto" }}
              >
                <option value="All">All</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>

        </div>
        <div
          className="my-3 rounded table-responsive-sm"
          style={{ overflowX: "auto" }}
        >
          <table id="my-table" className="table table-bordered mb-0">
            <thead className="t_head">
              <tr className="text-center align-middle">
                <th className="align-middle">Sl No</th>
                <th className="align-middle">Employee Name</th>
                <th className="align-middle">Employee No</th>
                <th className="align-middle">Loan No</th>
                <th className="align-middle">Loan Date</th>
                <th className="align-middle">Loan Type</th>
                <th className="align-middle">Loan Amount</th>
                <th className="align-middle">Total Repayment</th>
                <th className="align-middle">Balance Repayment</th>
                <th className="align-middle">Status</th>

                {/* <th className="align-middle">Repayment</th> */}
                {/* <th className="align-middle">Report</th> */}
                <th className="text-center align-middle">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="15">
                    <div className="d-flex justify-content-center m-3">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : currentLoanItems.length === 0 ? (
                <tr>
                  <td colSpan="15">
                    <div className="d-flex justify-content-center m-1">
                      <h6>Data Not Found</h6>
                    </div>
                  </td>
                </tr>
              ) : (
                currentLoanItems.map((loanItem, index) => {
                  const totalRepayment = loanItem.repayments
                    ? loanItem.repayments.reduce(
                        (acc, cur) => acc + cur.amount,
                        0,
                      )
                    : 0;
                  const balanceRepayment = loanItem.loanAmount - totalRepayment;

                  return (
                    <tr key={loanItem._id || index}>
                      <td>{currentPage * itemsPerPage + index + 1}</td>
                      <td>{loanItem.employee?.name || "N/A"}</td>
                      <td>{loanItem.employee?.employeeCode || "N/A"}</td>
                      <td>{loanItem.applicationNo || "N/A"}</td>
                      <td>
                        {loanItem.loanDate
                          ? new Date(loanItem.loanDate).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td>{loanItem.loanType || "N/A"}</td>
                      <td>$ {formatPrice(loanItem.loanAmount) || "N/A"}</td>
                      <td>$ {formatPrice(loanItem.paidAmount) || "N/A"}</td>
                      <td>$ {formatPrice(loanItem.balanceAmount) || "N/A"}</td>
                      <td className="status">
                        {["admin", "superadmin"].includes(userRole) &&
                        loanItem.status === "Pending" ? (
                          <>
                            <button
                              className="btn btn-secondary btn-sm me-1"
                              onClick={() =>
                                handleStatusChange(loanItem, "Approved")
                              }
                            >
                              Approve
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() =>
                                handleStatusChange(loanItem, "Rejected")
                              }
                            >
                              Reject
                            </button>
                          </>
                        ) : (
                          loanItem.status || "N/A"
                        )}
                      </td>
                      <td>
                        <div className="d-flex justify-content-center align-items-center gap-2">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => {
                              setSelectedData(loanItem);
                              setIsViewMode(true);
                            }}
                          >
                            <i className="bi bi-folder2-open"></i>
                          </button>
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => {
                              setSelectedData(loanItem);
                              setShowForm(true);
                            }}
                          >
                            <i className="bi bi-pencil-square"></i>
                          </button>
                          {["superadmin"].includes(userRole) && (
                            <>
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDelete({ loanItem })}
                              >
                                <i className="bi bi-trash3"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <ReactPaginate
          breakLabel="..."
          nextLabel="next >"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="< previous"
          renderOnZeroPageCount={null}
          containerClassName="pagination justify-content-center pagination-sm"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          activeClassName="active"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          disabledClassName="disabled"
        />
        {isViewMode && (
          <LoanView
            loan={selectedData}
            isViewMode={isViewMode}
            userRole={userRole}
            formatPrice={formatPrice}
            setIsViewMode={setIsViewMode}
          />
        )}
        {showForm && (
          <LoanForm
            loan={selectedData}
            companyCode={companyCode}
            companyId={companyId}
            employee={employee}
            selectedCompany={selectedCompany}
            onClose={() => {
              setShowForm(false);
              fetchLoan();
            }}
          />
        )}

        {/* {showReport && (
        <ViewReport
          loan={selectedData}
          companyCode={companyCode}
          companyId={companyId}
          employee={employee}
          formatPrice={formatPrice}
          selectedCompany={selectedCompany}
          onClose={() => {
            setShowReport(false);
            fetchLoan();
          }}
        />
      )} */}

        {/* Status Confirmation Modal */}
        {isStatusModalOpen && (
          <div
            className={`modal fade ${isStatusModalOpen ? "show d-block" : ""}`}
            style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="statusModalLabel"
            aria-hidden={!isStatusModalOpen}
            onClick={(e) => {
              if (e.target.classList.contains("modal")) cancelStatusChange();
            }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="statusModalLabel">
                    Confirm {statusAction} Loan
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={cancelStatusChange}
                  ></button>
                </div>
                <div className="modal-body">
                  Are you sure you want to {statusAction.toLowerCase()} this
                  loan? This action cannot be undone.
                </div>
                <div className="modal-footer">
                  <button className="button-2" onClick={cancelStatusChange}>
                    Cancel
                  </button>
                  <button className="button-2" onClick={confirmStatusChange}>
                    {statusAction}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div
            className={`modal fade ${isDeleteModalOpen ? "show d-block" : ""}`}
            style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="deleteModalLabel"
            aria-hidden={!isDeleteModalOpen}
            onClick={(e) => {
              if (e.target.classList.contains("modal"))
                setIsDeleteModalOpen(false);
            }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="deleteModalLabel">
                    Confirm Deletion
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setIsDeleteModalOpen(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  Are you sure you want to delete this loan? This action cannot
                  be undone.
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setIsDeleteModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-danger" onClick={confirmDelete}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanList;
